import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { getPendingRecords, removeRecord, updateRecord, clearQueue, AttendanceRecord } from "./attendanceQueueStorage";
import { BulkAddEmployeeAttendanceAPI, AddEmployeeAttendanceAPI } from "../Admin/Redux/employeeAttendance";
import { uploadWithThumbnail } from "./awsUpload";
import { DecriptEnv } from "./utils";
import { userStorage } from "./userStorage";
import { useSocket } from "../contexts/SocketContext";
import { Network } from "@capacitor/network";
import moment from "moment";
import { serverHealthService } from "./serverHealthService";
let globalIsSyncing = false;
let globalIsOffline = false;

export const useAttendanceSync = () => {
  const dispatch = useDispatch();
  const { socket, isConnected } = useSocket();
  const isConnectedRef = useRef(isConnected);

  useEffect(() => {
    isConnectedRef.current = isConnected;
    if (isConnected) {
      console.log(`[${moment().format("DD-MM-YYYY HH:mm:ss")}] [AttendanceSync] Socket connected to server. Resetting offline state and triggering sync...`);
      globalIsOffline = false;
      processQueue();
    }
  }, [isConnected]);

  const processQueue = async () => {
    console.log(`[${moment().format("DD-MM-YYYY HH:mm:ss")}] calling globalIsSyncing`, globalIsSyncing, "globalIsOffline", globalIsOffline);
    if (globalIsSyncing || globalIsOffline) return;

    try {
      const records = await getPendingRecords();
      if (records.length === 0) {
        return;
      }

      // Check backend health (/health) ONLY when there are pending records to upload
      const isServerHealthy = await serverHealthService.pingServer();
      if (!isServerHealthy) {
        console.log(`[${moment().format("DD-MM-YYYY HH:mm:ss")}] [AttendanceSync] Server /health check failed. Skipping /bulk-offline.`);
        serverHealthService.startServerReconnectionPolling();
        return;
      }

      globalIsSyncing = true;
      console.log(`[AttendanceSync] Found ${records.length} pending offline records. Syncing in bulk...`);

      const payloads = records.map(record => ({
        user: record.user,
        type: record.type,
        timestamp: record.timestamp,
        ppe_compliant: record.ppe_compliant,
        biometric_authentication_by: "face",
        biometric_authentication: {
          face: { photo: "", Signature: record.signature || [] },
        },
        photoBase64: record.photoBase64 || "",
        is_tampered: record.is_tampered,
        id: record.id,
      }));

      try {
        console.log(`[AttendanceSync] Calling BulkAddEmployeeAttendanceAPI. globalIsOffline: ${globalIsOffline}`);
        await new Promise<void>((resolve, reject) => {
          (dispatch as any)(BulkAddEmployeeAttendanceAPI(
            payloads,
            (result) => resolve(result),
            (err: any) => reject(err)
          ));
        });

        // Bulk add succeeded -> server is definitely online and working!
        serverHealthService.markOnline();

        // If bulk add succeeds, remove all processed records
        for (const record of records) {
          await removeRecord(record.id!);
        }
        // Ensure entire offline queue is cleared
        await clearQueue();

        // Trigger /sync-data immediately after bulk offline sync succeeds
        window.dispatchEvent(new Event("trigger-employee-sync"));

        // Emit socket events to trigger sync on other devices, just like single API
        if (socket && socket.connected) {
          socket.emit("employee-attendance-updated");
          socket.emit("hr-dashboard-availability-update");
        }
      } catch (err: any) {
        console.error(`[AttendanceSync] Bulk sync failed:`, err);
        const isNetworkError =
          (err as any)?.message?.includes("No internet connection") ||
          (err as any)?.message?.includes("Network Error") ||
          (err as any)?.message?.includes("Network Unavailable") ||
          (err as any)?.message?.includes("Cannot read properties of undefined") ||
          ((err as any)?.isAxiosError && !(err as any)?.response);

        if (isNetworkError) {
          console.log("Network or server unavailable during bulk sync, leaving records in queue.");
          // Mark server as offline so next checks know server has an issue
          serverHealthService.markOffline();

          // Only mark as offline if the physical network (Wi-Fi/data) is actually disconnected.
          try {
            const status = await Network.getStatus();
            if (!status.connected) {
              globalIsOffline = true;
            }
          } catch (e) { }

          // Start health check polling (every 30s) from this centralized service
          serverHealthService.startServerReconnectionPolling();
        } else {
          // If it's a data validation error, remove records to prevent getting stuck in a loop
          for (const record of records) {
            await removeRecord(record.id!);
          }
        }
      }
    } catch (e) {
      console.error("[AttendanceSync] Queue processing error:", e);
    } finally {
      globalIsSyncing = false;
    }
  };

  useEffect(() => {
    let interval: NodeJS.Timeout;
    let isAutoSyncEnabled = false;
    let networkListener: any;

    const initializeNetworkCheck = async () => {
      const status = await Network.getStatus();
      if (!status.connected) globalIsOffline = true;
    };
    initializeNetworkCheck();

    networkListener = Network.addListener(
      "networkStatusChange",
      (status) => {
        console.log('useAttendanceSync Network status changed:', status.connected ? 'Online' : 'Offline', status);
        if (status.connected) {
          globalIsOffline = false;
          // Trigger bulk offline immediately when the network connects again
          processQueue();

          // Dispatch to open NetworkRefreshModal if socket is not connected natively
          setTimeout(() => {
            if (!isConnectedRef.current) {
              console.log("calling INCREMENT_NETWORK_MODAL_COUNT")
              dispatch({ type: "INCREMENT_NETWORK_MODAL_COUNT" });
            }
          }, 3000);
        } else {
          globalIsOffline = true;
          dispatch({ type: "RESET_NETWORK_MODAL_COUNT" });
        }
      },
    );

    const handleSocketConnect = () => {
      console.log("[AttendanceSync] Socket connect event received. Resetting offline flag and triggering queue sync...");
      globalIsOffline = false;
      processQueue();
    };

    if (socket) {
      // Whenever the socket reconnects, it means the server is back online! Trigger a sync immediately!
      socket.on("connect", handleSocketConnect);
    }

    window.addEventListener("employee-sync-completed", processQueue);

    const setupInterval = async () => {
      try {
        const settings = await userStorage.getBusinessSettings();
        let intervalMs = 30000; // default 30 seconds

        if (settings?.isAutoSyncMultipleDeviceEnabled) {
          isAutoSyncEnabled = true;
          const durationMinutes = settings?.autoSyncMultipleDeviceDuration || 0;

          const scheduleNextSync = () => {
            let delayMs = 5000; // instant sync (5 seconds) by default if enabled but 0
            if (durationMinutes > 0) {
              const now = new Date();
              const msSinceMinute = (now.getSeconds() * 1000) + now.getMilliseconds();
              const currentMinute = now.getMinutes();
              const minutesToNext = durationMinutes - (currentMinute % durationMinutes);
              delayMs = (minutesToNext * 60000) - msSinceMinute;
            }

            interval = setTimeout(() => {
              processQueue();
              scheduleNextSync();
            }, delayMs);
          };

          scheduleNextSync();
        } else {
          // default 30 seconds if auto sync is disabled
          interval = setInterval(processQueue, 30000);
        }

        if (isAutoSyncEnabled && socket) {
          socket.on("employee-attendance-updated", processQueue);
        }
      } catch (e) {
        interval = setInterval(processQueue, 30000);
      }
    };

    setupInterval();

    const handleTrigger = () => {
      processQueue();
    };
    window.addEventListener("trigger-attendance-sync", handleTrigger);

    return () => {
      clearInterval(interval);
      window.removeEventListener("trigger-attendance-sync", handleTrigger);
      window.removeEventListener("employee-sync-completed", processQueue);
      if (socket) {
        socket.off("employee-attendance-updated", processQueue);
        socket.off("connect", handleSocketConnect);
      }
      if (networkListener) {
        networkListener.then((listener: any) => listener.remove());
      }
    };
  }, [dispatch, socket]);

  const triggerAttendanceSync = async () => {
    try {
      const status = await Network.getStatus();
      if (status.connected) {
        globalIsOffline = false;
      }
    } catch (e) { }
    await processQueue();
  };

  return { triggerAttendanceSync };
};
